
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Lock, ClipboardList, ExternalLink } from 'lucide-react';
import ChatBot from '../components/ChatBot';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Providers = () => {
  const { isAuthenticated, login, toggleProviderView, isProviderView } = useAuth();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const success = login(password);
    
    if (!success) {
      toast({
        title: "Login failed",
        description: "Incorrect password. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Login successful",
        description: "You now have access to provider information",
      });
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Providers - AutoPilot Care</title>
        <meta name="description" content="Resources and information for healthcare providers." />
        <meta property="og:title" content="Providers - AutoPilot Care" />
        <meta property="og:description" content="Resources and information for healthcare providers." />
        <meta property="og:image" content="/images/autopilot-og-image.png?v=6" />
        <meta property="og:updated_time" content="2024-04-11T00:00:00Z" />
        <meta property="og:url" content="https://autopilotcare.com/providers?v=6" />
      </Helmet>
      <Navbar />
      
      <main className="flex-grow pt-8">
        <div className="container mx-auto px-4 mb-12">
          <h1 className="text-3xl font-bold mb-4">Providers</h1>
          <p className="text-lg text-muted-foreground mb-8">
            Access our resources, medication catalog, and request forms for healthcare providers.
          </p>
          
          {!isAuthenticated ? (
            <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md border border-gray-200">
              <div className="flex items-center gap-2 mb-4 text-primary">
                <Lock className="h-5 w-5" />
                <h2 className="text-xl font-semibold">Protected Content</h2>
              </div>
              <p className="mb-4 text-muted-foreground">
                This section contains pricing information for healthcare providers. 
                Please enter the password to access the resources.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="relative mb-4">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={toggleShowPassword}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <Button type="submit" className="w-full">Access Resources</Button>
              </form>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-4 text-primary">
                    <ClipboardList className="h-5 w-5" />
                    <h2 className="text-xl font-semibold">View Medication Information</h2>
                  </div>
                  <p className="mb-6">
                    View our medication catalog with detailed clinical information by enabling provider view in the navigation bar.
                  </p>
                  <Button 
                    className="w-full flex items-center justify-center"
                    onClick={toggleProviderView}
                  >
                    {isProviderView ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                    {isProviderView ? "Hide Clinical Details" : "Show Clinical Details"}
                  </Button>
                </div>
                
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-4 text-primary">
                    <ClipboardList className="h-5 w-5" />
                    <h2 className="text-xl font-semibold">Product Request Form</h2>
                  </div>
                  <p className="mb-6">
                    Need to request specific products or have custom medication requirements? 
                    Use our provider request form to submit your needs.
                  </p>
                  <a 
                    href="https://forms.office.com/Pages/ResponsePage.aspx?id=1ugmNvYIC0G4Vq4wVsswxP3UMgC3YSVAtMNZy8wxNjJUMDBGT0ZVSktHQ0xXVVRCSDRNR1RFVjEwMS4u"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button className="w-full">Open Request Form</Button>
                  </a>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <h2 className="text-2xl font-bold mb-6">Medication Catalog</h2>
                <div className="overflow-x-auto">
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-primary">WEIGHT LOSS OPTIONS</h3>
                    
                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-lg">SEMAGLUTIDE INJECTION</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full table-auto border-collapse">
                            <thead>
                              <tr className="bg-muted">
                                <th className="border px-4 py-2 text-left">Product</th>
                                <th className="border px-4 py-2 text-left">CLINIC</th>
                                <th className="border px-4 py-2 text-left">PATIENT*</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border px-4 py-2">Semaglutide/Vitamin B12 (methyl) 5mg/1mg/ml injection 0.5ml</td>
                                <td className="border px-4 py-2">$54.00</td>
                                <td className="border px-4 py-2">$79.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Semaglutide/Vitamin B12 (methyl) 5mg/1mg/ml injection 1ml</td>
                                <td className="border px-4 py-2">$89.00</td>
                                <td className="border px-4 py-2">$115.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Semaglutide/Vitamin B12 (methyl) 5mg/1mg/ml injection 2ml</td>
                                <td className="border px-4 py-2">$150.00</td>
                                <td className="border px-4 py-2">$175.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Semaglutide/Vitamin B12 (methyl) 5mg/1mg/ml injection 3ml</td>
                                <td className="border px-4 py-2">$219.00</td>
                                <td className="border px-4 py-2">$249.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Semaglutide/ VITAMIN B6 (New!) 6.25mg/50mg/ml injection 2ml</td>
                                <td className="border px-4 py-2">$187.00</td>
                                <td className="border px-4 py-2">$217.00</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-lg">SEMAGLUTIDE SUBLINGUAL (GIVEN ONCE PER WEEK)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full table-auto border-collapse">
                            <thead>
                              <tr className="bg-muted">
                                <th className="border px-4 py-2 text-left">Product</th>
                                <th className="border px-4 py-2 text-left">CLINIC</th>
                                <th className="border px-4 py-2 text-left">PATIENT*</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border px-4 py-2">Semaglutide SUBLINGUAL 1.5mg troche #4</td>
                                <td className="border px-4 py-2">$26.00</td>
                                <td className="border px-4 py-2">$39.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Semaglutide SUBLINGUAL 3mg troche #4</td>
                                <td className="border px-4 py-2">$35.00</td>
                                <td className="border px-4 py-2">$49.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Semaglutide SUBLINGUAL 5.1mg troche #4</td>
                                <td className="border px-4 py-2">$49.00</td>
                                <td className="border px-4 py-2">$65.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Semaglutide SUBLINGUAL 7.2mg troche #4</td>
                                <td className="border px-4 py-2">$59.00</td>
                                <td className="border px-4 py-2">$79.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Semaglutide SUBLINGUAL 9.3mg troche #4</td>
                                <td className="border px-4 py-2">$72.00</td>
                                <td className="border px-4 py-2">$92.00</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-lg">TIRZEPATIDE INJECTION</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full table-auto border-collapse">
                            <thead>
                              <tr className="bg-muted">
                                <th className="border px-4 py-2 text-left">Product</th>
                                <th className="border px-4 py-2 text-left">CLINIC</th>
                                <th className="border px-4 py-2 text-left">PATIENT*</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border px-4 py-2">Tirzepatide/Vitamin B12 (methyl) 10mg/0.5mg/ml injection 1ml</td>
                                <td className="border px-4 py-2">$99.00</td>
                                <td className="border px-4 py-2">$125.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Tirzepatide/Vitamin B12 (methyl) 10mg/0.5mg/ml injection 2ml</td>
                                <td className="border px-4 py-2">$175.00</td>
                                <td className="border px-4 py-2">$200.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Tirzepatide/Vitamin B12 (methyl) 10mg/0.5mg/ml injection 3ml</td>
                                <td className="border px-4 py-2">$255.00</td>
                                <td className="border px-4 py-2">$295.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Tirzepatide/Vitamin B12 (methyl) 10mg/0.5mg/ml injection 4ml</td>
                                <td className="border px-4 py-2">$295.00</td>
                                <td className="border px-4 py-2">$315.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Tirzepatide/Vitamin B12 (methyl) 25mg/0.5mg/ml injection 2ml</td>
                                <td className="border px-4 py-2">$295.00</td>
                                <td className="border px-4 py-2">$345.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Tirzepatide/Vitamin B12 (methyl) 25mg/0.5mg/ml injection 3ml</td>
                                <td className="border px-4 py-2">$435.00</td>
                                <td className="border px-4 py-2">$485.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Tirzepatide/ VITAMIN B6 (New!) 12.5mg/50mg/ml injection 2ml</td>
                                <td className="border px-4 py-2">$218.00</td>
                                <td className="border px-4 py-2">$268.00</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-lg">TIRZEPATIDE SUBLINGUAL (GIVEN ONCE PER WEEK)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full table-auto border-collapse">
                            <thead>
                              <tr className="bg-muted">
                                <th className="border px-4 py-2 text-left">Product</th>
                                <th className="border px-4 py-2 text-left">CLINIC</th>
                                <th className="border px-4 py-2 text-left">PATIENT*</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border px-4 py-2">Tirzepatide SUBLINGUAL 13.4mg troche #4</td>
                                <td className="border px-4 py-2">$89.00</td>
                                <td className="border px-4 py-2">$115.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Tirzepatide SUBLINGUAL 20mg troche #4</td>
                                <td className="border px-4 py-2">$129.00</td>
                                <td className="border px-4 py-2">$159.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Tirzepatide SUBLINGUAL 26.7mg troche #4</td>
                                <td className="border px-4 py-2">$165.00</td>
                                <td className="border px-4 py-2">$205.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Tirzepatide SUBLINGUAL 33.3mg troche #4</td>
                                <td className="border px-4 py-2">$205.00</td>
                                <td className="border px-4 py-2">$239.00</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Tirzepatide SUBLINGUAL 40mg troche #4</td>
                                <td className="border px-4 py-2">$239.00</td>
                                <td className="border px-4 py-2">$299.00</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Tirzepatide SUBLINGUAL 46.7mg troche #4</td>
                                <td className="border px-4 py-2">$269.00</td>
                                <td className="border px-4 py-2">$329.00</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-lg">OTHER WEIGHT LOSS PRODUCTS</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full table-auto border-collapse">
                            <thead>
                              <tr className="bg-muted">
                                <th className="border px-4 py-2 text-left">Product</th>
                                <th className="border px-4 py-2 text-left">CLINIC</th>
                                <th className="border px-4 py-2 text-left">PATIENT*</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border px-4 py-2">Phentermine 37.5mg oral (MFG) #30</td>
                                <td className="border px-4 py-2">$17.99</td>
                                <td className="border px-4 py-2">N/A</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Tesofensine 250mcg capsules #30</td>
                                <td className="border px-4 py-2">$65.00</td>
                                <td className="border px-4 py-2">N/A</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Tesofensine 500mcg capsules #30</td>
                                <td className="border px-4 py-2">$95.00</td>
                                <td className="border px-4 py-2">N/A</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Tesofensine 1mg capsules #30</td>
                                <td className="border px-4 py-2">$159.00</td>
                                <td className="border px-4 py-2">N/A</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Topiramate 25mg tablets (MFG) #30</td>
                                <td className="border px-4 py-2">$8.99</td>
                                <td className="border px-4 py-2">N/A</td>
                              </tr>
                              <tr className="bg-muted/50">
                                <td className="border px-4 py-2">Topiramate 25mg tablets (MFG) #60</td>
                                <td className="border px-4 py-2">$9.99</td>
                                <td className="border px-4 py-2">N/A</td>
                              </tr>
                              <tr>
                                <td className="border px-4 py-2">Topiramate 100mg tablets (MFG) #30</td>
                                <td className="border px-4 py-2">$14.99</td>
                                <td className="border px-4 py-2">N/A</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-primary">WOMEN'S HEALTH/HORMONES</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto border-collapse">
                        <thead>
                          <tr className="bg-muted">
                            <th className="border px-4 py-2 text-left">Product</th>
                            <th className="border px-4 py-2 text-left">CLINIC</th>
                            <th className="border px-4 py-2 text-left">PATIENT</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border px-4 py-2">Estradiol 0.5mg/gram – Topi-click container 60gm</td>
                            <td className="border px-4 py-2">$55.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Estriol Vaginal Crm 0.2mg/gram – Topi-click/single dose syringes 30gm</td>
                            <td className="border px-4 py-2">$45.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Gabapentin 10% - Topi-click container/Pump 30gm</td>
                            <td className="border px-4 py-2">$75.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Lidocaine 5% cream – Pump 30gm</td>
                            <td className="border px-4 py-2">$45.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Lidocaine 5% cream – Pump 60gm</td>
                            <td className="border px-4 py-2">$60.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Lidocaine 5%/Estradiol 0.5mg/gram – Topi-click container 30gm</td>
                            <td className="border px-4 py-2">$49.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Lidocaine 5%/Estradiol 0.5mg/gram – Topi-click container 60gm</td>
                            <td className="border px-4 py-2">$64.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">BLT CREAM (Benzocaine 20%, Lidocaine 6%, Tetracaine 4%) 30gm</td>
                            <td className="border px-4 py-2">$56.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Scream Cream (Sildenafil 2%/Aminophylline 3%/Arginine 6%) 15gm</td>
                            <td className="border px-4 py-2">$40.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Amitriptyline 2%/Baclofen 2%/Gabapentin 2% cream 60gm</td>
                            <td className="border px-4 py-2">$62.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-primary">MEN'S HEALTH/HORMONES</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto border-collapse">
                        <thead>
                          <tr className="bg-muted">
                            <th className="border px-4 py-2 text-left">Product</th>
                            <th className="border px-4 py-2 text-left">CLINIC</th>
                            <th className="border px-4 py-2 text-left">PATIENT</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border px-4 py-2">Testosterone Cypionate 250mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$65.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Testosterone Cypionate/Enanthate (90/10) 200mg/ml injection 5ml</td>
                            <td className="border px-4 py-2">$35.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Testosterone Cypionate/Enanthate/Propionate 250mg/ml injection (100mg/100mg/50mg/ml) 5ml</td>
                            <td className="border px-4 py-2">$50.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Testosterone Enanthate 250mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$65.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Testosterone Cream (up to 200mg/ml) 30gm</td>
                            <td className="border px-4 py-2">$35.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Testosterone Troches (up to 99mg) each</td>
                            <td className="border px-4 py-2">$2.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Testosterone/Sermorelin/Oxytocin/Tadalafil Troches (20mcg/300mcg/100 units/25mg) each</td>
                            <td className="border px-4 py-2">$7.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Anastrazole 0.5mg capsule each</td>
                            <td className="border px-4 py-2">$0.80</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Anastrazole 1mg tablet each</td>
                            <td className="border px-4 py-2">$1.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Clomiphene 25mg or 55mg capsule each</td>
                            <td className="border px-4 py-2">$1.65</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">DIM/Clomiphene 200mg/25mg capsules #30</td>
                            <td className="border px-4 py-2">$35.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">DIM/Clomiphene 200mg/35mg capsules #30</td>
                            <td className="border px-4 py-2">$40.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Enclomiphene 25mg capsule each</td>
                            <td className="border px-4 py-2">$3.25</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Finasteride 1mg or 5mg tablet (MFG) each</td>
                            <td className="border px-4 py-2">$1.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Finasteride 1mg/Minoxidil 2.5mg capsule each</td>
                            <td className="border px-4 py-2">$1.80</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Gonadorelin 500mg mcg troche each</td>
                            <td className="border px-4 py-2">$2.90</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Gonadorelin 0.2mg/ml injection 5ml</td>
                            <td className="border px-4 py-2">$29.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Low-dose Naltrexone (1mg to 3mg) capsules each</td>
                            <td className="border px-4 py-2">$1.35</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Oxytocin 100 i.u. troche each</td>
                            <td className="border px-4 py-2">$4.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Sildenafil 50 to 110mg troche each</td>
                            <td className="border px-4 py-2">$4.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Tamoxifen 10mg or 20mg tablet (MFG) each</td>
                            <td className="border px-4 py-2">$1.50</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Tamsulosin 0.4mg capsule (MFG) each</td>
                            <td className="border px-4 py-2">$1.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Tadalafil 5 to 25mg troche each</td>
                            <td className="border px-4 py-2">$5.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Tadalafil 40mg troche each</td>
                            <td className="border px-4 py-2">$6.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Tadalafil 80mg capsule each</td>
                            <td className="border px-4 py-2">$8.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Trimix – Papav./Phentolamine/Alprost. 30mg/2mg/10mcg/ml inj. 5ml</td>
                            <td className="border px-4 py-2">$59</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-primary">OTHER PEPTIDES</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto border-collapse">
                        <thead>
                          <tr className="bg-muted">
                            <th className="border px-4 py-2 text-left">Product</th>
                            <th className="border px-4 py-2 text-left">CLINIC</th>
                            <th className="border px-4 py-2 text-left">PATIENT</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border px-4 py-2">GHK-Cu 0.5% topical facial ointment 30gm</td>
                            <td className="border px-4 py-2">$95.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">GHK-Cu 0.5% topical hair solution 60ml</td>
                            <td className="border px-4 py-2">$159.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Rapamycin (Sirolimus) 6mg, 8mg or 9mg capsule each</td>
                            <td className="border px-4 py-2">$6.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Sermorelin 1mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$75.00</td>
                            <td className="border px-4 py-2">$85.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">5 Amino 1-MQ 50mg capsules #40</td>
                            <td className="border px-4 py-2">$129.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-primary">OTHER INJECTABLE/WELLNESS PRODUCTS</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto border-collapse">
                        <thead>
                          <tr className="bg-muted">
                            <th className="border px-4 py-2 text-left">Product</th>
                            <th className="border px-4 py-2 text-left">CLINIC</th>
                            <th className="border px-4 py-2 text-left">PATIENT</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border px-4 py-2">Ascorbic Acid (tapioca) 450mg/ml injection 50ml</td>
                            <td className="border px-4 py-2">$38.00</td>
                            <td className="border px-4 py-2">$48.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">B Complex (Niacinamide/Pyridox./Thiamine/Leucine/Dexpanth./Riboflavin) 25mg/25mg/25mg/2.5mg/2mg/2mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$27.00</td>
                            <td className="border px-4 py-2">$37.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Calcium Chloride 10% 100mg/ml injection 10ml (MFG)</td>
                            <td className="border px-4 py-2">$33.00</td>
                            <td className="border px-4 py-2">$43.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Calcium Gluconate 100mg/ml injection 10ml (MFG)</td>
                            <td className="border px-4 py-2">$40.00</td>
                            <td className="border px-4 py-2">$50.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Dexpanthenol (Vitamin B5) 250mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$27.00</td>
                            <td className="border px-4 py-2">$37.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">D3 50,000 i.u./ml olive oil injection 5ml</td>
                            <td className="border px-4 py-2">$38.00</td>
                            <td className="border px-4 py-2">$48.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Glutathione 200mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$39.00</td>
                            <td className="border px-4 py-2">$49.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">ICB Complex (Inositol/Choline/B1/B2/B3/B5/B6/B12/Leucine/Carnitine/Lidocaine) 25mg/25mg/5mg/2.5mg/25mg/5mg/5mg/0.1mg/1.5mg/25mg/10mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$29.00</td>
                            <td className="border px-4 py-2">$39.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">L-Carnitine 500mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$28.00</td>
                            <td className="border px-4 py-2">$38.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">L-Lysine 100mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$24.00</td>
                            <td className="border px-4 py-2">$34.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">LIPO-C – MIC/Methylcobalamin (B12)/L-Carnitine 25mg/50mg/50mg/1mg/100mg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$22.00</td>
                            <td className="border px-4 py-2">$32.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Magnesuim Chloride 200mg/ml injection 50ml (MFG)</td>
                            <td className="border px-4 py-2">$34.00</td>
                            <td className="border px-4 py-2">$44.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Methylcobalamin (B12) 1,000mcg/ml injection 10ml</td>
                            <td className="border px-4 py-2">$24.00</td>
                            <td className="border px-4 py-2">$34.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Methylcobalamin (B12) 1,000mcg/ml injection 30ml</td>
                            <td className="border px-4 py-2">$70.00</td>
                            <td className="border px-4 py-2">$80.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Methylene Blue 20mg/ml injection 5ml</td>
                            <td className="border px-4 py-2">$59.00</td>
                            <td className="border px-4 py-2">$69.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Methylene Blue 30mg capsules #90</td>
                            <td className="border px-4 py-2">$199.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">MIC (Methionine/Inositol/Choline) + Methylcobalamin 25mg/50mg/50mg/1mg/ml injection 30ml</td>
                            <td className="border px-4 py-2">$53.00</td>
                            <td className="border px-4 py-2">$63.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">NAD + (nicotinamide adenine dinucleotine) 200mg/ml injection 5ml</td>
                            <td className="border px-4 py-2">$72.00</td>
                            <td className="border px-4 py-2">$82.00</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">SLIM BOOST (Methionine, Inositol, Choline, L-Carnitine, Thiamine, Pyridoxine, B12) injection 30ml</td>
                            <td className="border px-4 py-2">$60.00</td>
                            <td className="border px-4 py-2">$70.00</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Sodium Bicarbonate 8.4% injection 50ml (MFG)</td>
                            <td className="border px-4 py-2">$45.00</td>
                            <td className="border px-4 py-2">$55.00</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-primary">SUPPLIES/SHIPPING</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto border-collapse">
                        <thead>
                          <tr className="bg-muted">
                            <th className="border px-4 py-2 text-left">Product</th>
                            <th className="border px-4 py-2 text-left">CLINIC</th>
                            <th className="border px-4 py-2 text-left">PATIENT</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border px-4 py-2">Syringes (any) /alcohol prep wipes 10 count</td>
                            <td className="border px-4 py-2">$3.99</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Bacteriostatic Water for injection 30ml</td>
                            <td className="border px-4 py-2">$10.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Bacteriostatic Water for injection 30ml pack of 25</td>
                            <td className="border px-4 py-2">$80.00</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr className="bg-muted/50">
                            <td className="border px-4 py-2">Overnight cold shipping (for clinic orders of $150+)</td>
                            <td className="border px-4 py-2">$19.95</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Overnight cold shipping (for clinic orders under $150)</td>
                            <td className="border px-4 py-2">$34.95</td>
                            <td className="border px-4 py-2">N/A</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mt-6 italic">
                  *Discounted patient pricing available for clinics sending larger quantities of GLP1 prescriptions per month
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-6">
                  <ClipboardList className="h-5 w-5 text-primary" />
                  <h2 className="text-2xl font-bold">Product Request Form</h2>
                </div>
                
                <p className="mb-6 text-muted-foreground">
                  Need to request specific products or have custom medication requirements? Use our provider request form to submit your needs.
                </p>
                
                <a 
                  href="https://forms.office.com/Pages/ResponsePage.aspx?id=1ugmNvYIC0G4Vq4wVsswxP3UMgC3YSVAtMNZy8wxNjJUMDBGT0ZVSktHQ0xXVVRCSDRNR1RFVjEwMS4u" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Button className="w-full flex items-center justify-center py-6 text-lg">
                    Open Product Request Form
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
      <ChatBot />
    </div>
  );
};

export default Providers;
